import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, Observable, switchMap, take, tap, throwError } from 'rxjs';
import { ProductSharedervice } from 'src/app/core/product/product-shared.service';
import {
    Product,
    ProductCategory,
    ProductCounter,
} from 'src/app/core/product/product-shared.types';
import { ApiResponse } from 'src/app/core/utils/api-response';
import { environment } from 'src/environments/environment';

@Injectable({
    providedIn: 'root',
})
export class ProductService extends ProductSharedervice {

    // Local counter BehaviorSubject for product counters
    private _counter: BehaviorSubject<ProductCounter | null> =
        new BehaviorSubject<ProductCounter | null>(null);

        
    constructor(protected override _httpClient: HttpClient) {
        super(_httpClient);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------


    /**
     * Observable for product counter
     */
    get counter$(): Observable<ProductCounter | null> {
        return this._counter.asObservable();
    }


    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Export vendors as a file (e.g., Excel)
     */
    exportProducts(schoolCode: string, staffCode: string = ''): Observable<Blob> {
        let url = `${this.PRODUCT_URL}/${schoolCode}/export`;
        if (staffCode) url += `?staffCode=${encodeURIComponent(staffCode)}`;
        return this._httpClient.get(url, {
            responseType: 'blob'
        });
    }

    /**
     * Fetch paged products with optional search, sorting, and filters
     */
    getProductsPaged(
        schoolCode: string,
        search: string,
        page: number,
        size: number,
        sortField: string = '',
        sortOrder: number = 1,
        filters: { [key: string]: any } = {},
        staffCode: string = ''
    ): Observable<ApiResponse<{ content: Product[]; totalElements: number }>> {
        let url = `${this.PRODUCT_URL}/school/${schoolCode}/paged?page=${page}&size=${size}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        if (sortField) url += `&sort=${sortField},${sortOrder === 1 ? 'asc' : 'desc'}`;
        if (staffCode) url += `&staffCode=${encodeURIComponent(staffCode)}`;

        // Add filters as query params (array or object)
        Object.keys(filters).forEach(key => {
            const value = filters[key];
            if (Array.isArray(value)) {
                value.forEach(v => url += `&${key}=${encodeURIComponent(v)}`);
            } else if (value !== undefined && value !== null && value !== '') {
                url += `&${key}=${encodeURIComponent(value)}`;
            }
        });

        return this._httpClient.get<ApiResponse<{ content: Product[]; totalElements: number }>>(url)
            .pipe(
                tap((response) => {
                    if (response.success && response.data && Array.isArray(response.data.content)) {
                        // Handle successful response if needed
                        this._products.next(response.data.content);
                    }
                }),
                catchError((error) => {
                    // Handle error
                    return throwError(() => error);
                })
            );
    }

    /**
     * Fetch product counter for a school
     */
    getProductCounter(schoolCode: string): Observable<ApiResponse<ProductCounter>> {
        const url = `${this.PRODUCT_URL}/school/${schoolCode}/counter`;
        return this._httpClient.get<ApiResponse<ProductCounter>>(url).pipe(
            tap((response) => {
                if (!response.success) {
                    console.error(
                        'Failed to fetch product counter:',
                        response.message
                    );
                } else {
                    this._counter.next(response.data);
                }
            }),
            catchError((error) => {
                console.error('Error fetching product counter:', error);
                throw error;
            })
        );
    }

    /**
     * Increase product counters by key
     * @param key 'totalProducts' | 'totalCategories' | 'totalBrands'
     * @param amount number to increase (default 1)
     */
    increaseCounter(key: keyof ProductCounter, amount: number = 1): void {
        const current = this._counter.value;
        if (current && typeof current[key] === 'number') {
            this._counter.next({
                ...current,
                [key]: (current[key] as number) + amount
            });
        }
    }

    /**
     * Decrease product counters by key
     * @param key 'totalProducts' | 'totalCategories' | 'totalBrands'
     * @param amount number to decrease (default 1)
     */
    decreaseCounter(key: keyof ProductCounter, amount: number = 1): void {
        const current = this._counter.value;
        if (current && typeof current[key] === 'number') {
            this._counter.next({
                ...current,
                [key]: Math.max(0, (current[key] as number) - amount)
            });
        }
    }

    /**
     * Add product by code
     */
    addProduct(product: Partial<Product>): Observable<ApiResponse<Product>> {
        const url = `${this.PRODUCT_URL}`;
        return this._httpClient.post<ApiResponse<Product>>(url, product).pipe(
            tap((response) => {
                if (response.success) {
                    console.log('Product added successfully:', response.data);
                }
            }),
            catchError((error) => {
                console.error('Failed to add product:', error);
                const errorResponse: ApiResponse<Product> = error?.error || {
                    success: false,
                    message: 'Failed to add product',
                    data: null,
                    timestamp: new Date(),
                };
                return throwError(() => errorResponse);
            })
        );
    }

    /**
     * Update product by code
     */
    updateProduct(
        code: string,
        productData: Partial<Product>
    ): Observable<ApiResponse<Product>> {
        const url = `${this.PRODUCT_URL}/code/${code}`;
        return this._httpClient
            .put<ApiResponse<Product>>(url, productData)
            .pipe(
                tap((response) => {
                    if (response.success) {
                        console.log(
                            'Product updated successfully:',
                            response.data
                        );
                    }
                }),
                catchError((error) => {
                    console.error('Failed to update product:', error);
                    const errorResponse: ApiResponse<Product> =
                        error?.error || {
                            success: false,
                            message: 'Failed to update product',
                            data: null,
                            timestamp: new Date(),
                        };
                    return throwError(() => errorResponse);
                })
            );
    }

    /**
     * Delete a product by its code
     */
    deleteProduct(productCode: string): Observable<ApiResponse<void>> {
        return this._httpClient
            .delete<ApiResponse<void>>(
                `${this.PRODUCT_URL}/code/${productCode}`
            )
            .pipe(
                tap((response) => {
                    if (response.success) {
                        console.log('Product deleted successfully');
                    }
                }),
                catchError((error) => {
                    console.error('Failed to delete product:', error);
                    const errorResponse: ApiResponse<void> = error?.error || {
                        success: false,
                        message: 'Failed to delete product',
                        data: null,
                        timestamp: new Date(),
                    };
                    return throwError(() => errorResponse);
                })
            );
    }

    /**
     * Add a product to favorites
     */
    addToFavorite(
        productCode: string,
        schoolCode: string,
        favourite: boolean,
        staffCode: string
    ): Observable<ApiResponse<void>> {
        const url = `${this.PRODUCT_URL}/${productCode}/school/${schoolCode}/favourite?favourite=${favourite}&staffCode=${encodeURIComponent(staffCode)}`;
        return this._httpClient.patch<ApiResponse<void>>(url, {}).pipe(
            tap((response) => {
                if (response.success) {
                    console.log('Product added to favorites successfully');
                }
            }),
            catchError((error) => {
                console.error('Failed to add product to favorites:', error);
                const errorResponse: ApiResponse<void> = error?.error || {
                    success: false,
                    message: 'Failed to add product to favorites',
                    data: null,
                    timestamp: new Date(),
                };
                return throwError(() => errorResponse);
            })
        );
    }

    /**
     * Upload product photo
     */
    uploadProductPhoto(
        productCode: string,
        formData: FormData
    ): Observable<ApiResponse<string>> {
        const url = `${this.PRODUCT_URL}/code/${productCode}/photo`;
        return this._httpClient.post<ApiResponse<string>>(url, formData).pipe(
            tap((response) => {
                if (response.success) {
                    console.log('Photo uploaded successfully:', response.data);
                }
            }),
            catchError((error) => {
                const errorResponse: ApiResponse<{ url: string }> =
                    error?.error || {
                        success: false,
                        message:
                            error.error?.message || 'Failed to upload photo',
                        data: null,
                        timestamp: new Date(),
                    };
                return throwError(() => errorResponse);
            })
        );
    }

    /**
     * Create a new product category and update local categories
     */
    createCategory(
        category: ProductCategory
    ): Observable<ApiResponse<ProductCategory>> {
        return this.categories$.pipe(
            take(1),
            switchMap((categories) =>
                this._httpClient
                    .post<ApiResponse<ProductCategory>>(
                        this.PRODUCT_CATEGORIES_URL,
                        category
                    )
                    .pipe(
                        tap((response) => {
                            if (response.success && response.data) {
                                this._categories.next([
                                    response.data,
                                    ...(categories || []),
                                ]);
                            }
                        }),
                        catchError((error) => {
                            console.error(
                                'Failed to create product category:',
                                error
                            );
                            const errorResponse: ApiResponse<ProductCategory> =
                                error?.error || {
                                    success: false,
                                    message:
                                        'Failed to create product category',
                                    data: null,
                                    timestamp: new Date(),
                                };
                            return throwError(() => errorResponse);
                        })
                    )
            )
        );
    }

    /**
     * Update an existing product category and update local categories
     */
    updateCategory(
        category: ProductCategory
    ): Observable<ApiResponse<ProductCategory>> {
        return this.categories$.pipe(
            take(1),
            switchMap((categories) =>
                this._httpClient
                    .put<ApiResponse<ProductCategory>>(
                        `${this.PRODUCT_CATEGORIES_URL}/code/${category.code}`,
                        category
                    )
                    .pipe(
                        tap((response) => {
                            if (response.success && response.data) {
                                const index =
                                    categories?.findIndex(
                                        (item) => item.code === category.code
                                    ) ?? -1;
                                if (index !== -1 && categories) {
                                    categories[index] = response.data;
                                    this._categories.next(categories);
                                }
                            }
                        }),
                        catchError((error) => {
                            console.error(
                                'Failed to update product category:',
                                error
                            );
                            const errorResponse: ApiResponse<ProductCategory> =
                                error?.error || {
                                    success: false,
                                    message:
                                        'Failed to update product category',
                                    data: null,
                                    timestamp: new Date(),
                                };
                            return throwError(() => errorResponse);
                        })
                    )
            )
        );
    }

    /**
     * Delete a product category and update local categories
     */
    deleteCategory(code: string): Observable<ApiResponse<void>> {
        return this.categories$.pipe(
            take(1),
            switchMap((categories) =>
                this._httpClient
                    .delete<ApiResponse<void>>(
                        `${this.PRODUCT_CATEGORIES_URL}/code/${code}`
                    )
                    .pipe(
                        tap((response) => {
                            if (response.success && categories) {
                                const filteredCategories = categories.filter(
                                    (item) => item.code !== code
                                );
                                this._categories.next(filteredCategories);
                            }
                        }),
                        catchError((error) => {
                            console.error(
                                'Failed to delete product category:',
                                error
                            );
                            const errorResponse: ApiResponse<void> =
                                error?.error || {
                                    success: false,
                                    message:
                                        'Failed to delete product category',
                                    data: null,
                                    timestamp: new Date(),
                                };
                            return throwError(() => errorResponse);
                        })
                    )
            )
        );
    }


    private _refreshTrigger = new BehaviorSubject<boolean>(false);
      refreshTrigger$ = this._refreshTrigger.asObservable();

    triggerRefresh(): void {
      this._refreshTrigger.next(true);
    }

}